import React, { useCallback, useMemo } from 'react';
import '@reservamos/user-menu-widget';
import { UserMenuWidget } from '@reservamos/user-menu-widget/react';
import useWhitelabelFeatures from '../../hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from '../../hooks/whitelabel/useWhitelabelEnvs';
import usePurchase from '../../hooks/store/usePurchase';
import { useReserhubAccount } from '../../context/ReserhubAccount';
import { CurrentLang } from '../../utils/lang';
import { isWebView } from '../../utils/webview';

const DEFAULT_SITE_URL = '/';

/**
 * Get a value that can be safely passed as a custom element attribute.
 * @param {*} value - Value to normalize.
 * @returns {string|undefined}
 */
function attributeValue(value) {
  if (value === null || value === undefined || value === '') return undefined;
  return String(value);
}

/**
 * Get the available Doters balance from a connected account.
 * @param {object} dotersAccount - Connected Doters account.
 * @returns {string|undefined}
 */
function getDotersBalance(dotersAccount) {
  return attributeValue(
    dotersAccount?.wallet?.balance?.available ??
      dotersAccount?.wallet?.balance ??
      dotersAccount?.balance,
  );
}

/**
 * Get the current site's URL for widget account links.
 * @param {string} siteUrl - Configured site URL.
 * @returns {string}
 */
function getSiteUrl(siteUrl) {
  if (siteUrl) return siteUrl;
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return DEFAULT_SITE_URL;
}

/**
 * ReserhubUserMenu component.
 * Render the shared user menu widget with data already loaded by the host app.
 * @returns {JSX.Element}
 */
export default function ReserhubUserMenu() {
  const { user, isUserLoading, isUserFetched, nextPurchase, logout, connectedAccounts } =
    useReserhubAccount();
  const features = useWhitelabelFeatures();
  const environment = useWhitelabelEnvs();
  const purchase = usePurchase();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const isPurchasePending = pathname.includes('complete') && purchase?.state !== 'completed';

  const dotersAccount = connectedAccounts?.find((account) => account.provider === 'doters');

  const widgetConfig = useMemo(() => {
    const config = {
      lang: CurrentLang().split('-')[0],
      hostApp: 'funnel',
      loading: isUserLoading || !isUserFetched,
      redirectToCurrentUrl: true,
      siteUrl: getSiteUrl(environment.funnelUrl),
      homeUrl: environment.homeUrl,
      firstName: attributeValue(user?.firstName),
      lastName: attributeValue(user?.lastName),
      upcomingPurchase:
        !isPurchasePending && nextPurchase ? attributeValue(nextPurchase.token) : undefined,
      dotersBalance: getDotersBalance(dotersAccount),
      dotersMemberId: attributeValue(dotersAccount?.profile?.memberId),
      dotersLevel: attributeValue(dotersAccount?.profile?.level),
      dotersProgramUrl: attributeValue(dotersAccount?.profile?.termsAndConditionsUrl),
      ...(features.DOTERS_ENABLED && { dotersEnabled: true }),
    };

    return config;
  }, [
    dotersAccount,
    environment.funnelUrl,
    environment.homeUrl,
    features.DOTERS_ENABLED,
    isPurchasePending,
    isUserFetched,
    isUserLoading,
    nextPurchase,
    user,
  ]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  if (features.HIDE_RESERHUB_LOGIN_ON_WEBVIEW && isWebView()) {
    return null;
  }

  return <UserMenuWidget config={widgetConfig} onLogout={handleLogout} />;
}
